import { createInstancedRenderer } from "./instancedRenderer";
import { paintMarkers } from "../canvas2d/paintMarkers";

/** Shapes of markerRenderer instances. */
export const MARKER_SHAPE = {
  CIRCLE: 0,
  ARROW_UP: 1,
  ARROW_DOWN: 2,
};

// Depth of the notch in the base of an arrowhead, a share of its half size
const ARROW_NOTCH = "0.5";

// Instance: [x, y] in data units, [offset] down in CSS px, [shape, size in CSS px], [r, g, b, a]
const MARKER_ATTRIBUTE_COMPONENTS = [3, 2, 4];

// A square of `size` around the point moved by `offset`; the fragment
// shader cuts the shape out of it with a one-pixel soft edge
const MARKER_VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 aCorner;
layout(location = 1) in vec3 aPoint;
layout(location = 2) in vec2 aShape;
layout(location = 3) in vec4 aColor;

uniform vec2 uViewport;
uniform vec2 uX;
uniform vec2 uY;
uniform float uPixelRatio;

out vec4 vColor;
out vec2 vLocal;
flat out float vShape;

void main() {
  vec2 centre = vec2(aPoint.x * uX.x + uX.y, aPoint.y * uY.x + uY.y + aPoint.z * uPixelRatio);
  vec2 position = centre + (aCorner - 0.5) * aShape.y * uPixelRatio;
  vec2 clip = position / uViewport * 2.0 - 1.0;

  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  vColor = aColor;
  vLocal = aCorner * 2.0 - 1.0;
  vShape = aShape.x;
}
`;

// Signed distance to the shape in local units (inside < 0); y grows down.
// An arrowhead is a triangle with the apex at the top whose base has a notch
const MARKER_FRAGMENT_SHADER = `#version 300 es
precision mediump float;

in vec4 vColor;
in vec2 vLocal;
flat in float vShape;
out vec4 outColor;

void main() {
  float edgeDistance;

  if (vShape < 0.5) {
    edgeDistance = length(vLocal) - 1.0;
  } else {
    float y = vShape < 1.5 ? vLocal.y : -vLocal.y;

    float notch = y - 1.0 + ${ARROW_NOTCH} * (1.0 - abs(vLocal.x));

    edgeDistance = max(notch, abs(vLocal.x) - (y + 1.0) * 0.5);
  }

  float coverage = clamp(0.5 - edgeDistance / max(fwidth(edgeDistance), 0.0001), 0.0, 1.0);

  if (coverage <= 0.0) discard;

  outColor = vec4(vColor.rgb, vColor.a * coverage);
}
`;

/**
 * Instanced small shapes at data points: the dots of Parabolic SAR and the
 * arrowheads of Fractal. The size and the offset from the point are in CSS
 * px, so they stay the same on zoom.
 */
export const createMarkerRenderer = () => {
  const renderer = createInstancedRenderer({
    vertexShader: MARKER_VERTEX_SHADER,
    fragmentShader: MARKER_FRAGMENT_SHADER,
    uniformNames: ["uViewport", "uX", "uY", "uPixelRatio"],
    attributeComponents: MARKER_ATTRIBUTE_COMPONENTS,
  });

  return {
    floatsPerInstance: renderer.floatsPerInstance,
    init: renderer.init,
    release: renderer.release,
    setInstances: renderer.setInstances,
    updateInstances: renderer.updateInstances,

    /**
     * @param {object} params
     * @param {number[]} params.viewport   - [width, height] of the canvas, device px
     * @param {number[]} params.xTransform - [scale, offset]: data x → device px
     * @param {number[]} params.yTransform - [scale, offset]: data y → device px
     * @param {number} params.pixelRatio
     */
    draw: (params) => {
      const { viewport, xTransform, yTransform, pixelRatio } = params;

      renderer.draw(
        (gl, uniforms) => {
          gl.uniform2fv(uniforms.uViewport, viewport);
          gl.uniform2fv(uniforms.uX, xTransform);
          gl.uniform2fv(uniforms.uY, yTransform);
          gl.uniform1f(uniforms.uPixelRatio, pixelRatio);
        },
        (context, instances) => paintMarkers(context, instances, renderer.floatsPerInstance, params),
      );
    },
  };
};
